/**
 * Comprehensive troubleshooting utilities with health checks, diagnostics, and automated problem detection
 */

import { logger } from '../logger/index.js';
import type { LogContext } from '../logger/index.js';
import { debug } from '../debug/index.js';
import { errorHandling, type CategorizedError } from '../error-handling/index.js';
import { typeSafety } from '../type-safety/index.js';

// Branded types for troubleshooting
declare const HealthCheckIdBrand: unique symbol;
export type HealthCheckId = string & { readonly [HealthCheckIdBrand]: typeof HealthCheckIdBrand };

declare const DiagnosticIdBrand: unique symbol;
export type DiagnosticId = string & { readonly [DiagnosticIdBrand]: typeof DiagnosticIdBrand };

// Health check types
export type HealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

export interface HealthCheck {
  readonly id: HealthCheckId;
  readonly name: string;
  readonly description: string;
  readonly category: 'system' | 'database' | 'network' | 'external' | 'business';
  readonly critical: boolean;
  readonly timeout: number;
  readonly check: () => Promise<HealthCheckResult>;
}

export interface HealthCheckResult {
  readonly status: HealthStatus;
  readonly message: string;
  readonly details?: Record<string, any>;
  readonly timestamp: number;
  readonly duration: number;
  readonly error?: CategorizedError;
}

export interface SystemHealth {
  readonly overall: HealthStatus;
  readonly checks: Map<HealthCheckId, HealthCheckResult>;
  readonly summary: {
    readonly total: number;
    readonly healthy: number;
    readonly degraded: number;
    readonly unhealthy: number;
    readonly unknown: number;
  };
  readonly criticalIssues: HealthCheckResult[];
  readonly timestamp: number;
}

// Diagnostic types
export interface Diagnostic {
  readonly id: DiagnosticId;
  readonly name: string;
  readonly description: string;
  readonly category: 'performance' | 'configuration' | 'connectivity' | 'data' | 'security';
  readonly run: (context?: LogContext) => Promise<DiagnosticResult>;
}

export interface DiagnosticResult {
  readonly status: 'passed' | 'failed' | 'warning' | 'info';
  readonly message: string;
  readonly details?: Record<string, any>;
  readonly recommendations?: string[];
  readonly timestamp: number;
  readonly duration: number;
}

// Health check manager
export class HealthCheckManager {
  private static readonly checks = new Map<HealthCheckId, HealthCheck>();
  private static readonly results = new Map<HealthCheckId, HealthCheckResult>();

  /**
   * Register a health check
   */
  static register(check: HealthCheck): void {
    this.checks.set(check.id, check);
    logger.debug('Health check registered', {
      checkId: check.id,
      name: check.name,
      category: check.category,
      critical: check.critical
    });
  }

  /**
   * Run a specific health check
   */
  static async runCheck(checkId: HealthCheckId, context?: LogContext): Promise<HealthCheckResult> {
    const check = this.checks.get(checkId);
    if (!check) {
      throw new Error(`Health check not found: ${checkId}`);
    }

    const startTime = performance.now();
    const sessionId = debug.session.createSession({
      operation: 'health_check',
      checkId,
      checkName: check.name,
      ...context
    });

    try {
      logger.debug('Running health check', {
        checkId,
        name: check.name,
        category: check.category,
        ...context
      });

      const result = await Promise.race([
        check.check(),
        new Promise<never>((_, reject) => 
          setTimeout(() => reject(new Error('Health check timeout')), check.timeout)
        )
      ]);

      const duration = performance.now() - startTime;
      const healthResult: HealthCheckResult = {
        ...result,
        duration,
        timestamp: Date.now()
      };

      this.results.set(checkId, healthResult);

      logger.debug('Health check completed', {
        checkId,
        name: check.name,
        status: result.status,
        duration,
        ...context
      });

      return healthResult;
    } catch (error) {
      const duration = performance.now() - startTime;
      const categorizedError = errorHandling.createError('UNKNOWN', 
        (error as Error).message, {
          originalError: error as Error,
          context: { checkId, checkName: check.name }
        });

      const healthResult: HealthCheckResult = {
        status: 'unhealthy',
        message: `Health check failed: ${(error as Error).message}`,
        duration,
        timestamp: Date.now(),
        error: categorizedError
      };

      this.results.set(checkId, healthResult);

      debug.session.addError(sessionId, error as Error, {
        checkId,
        checkName: check.name,
        duration
      });

      logger.error('Health check failed', {
        checkId,
        name: check.name,
        error: (error as Error).message,
        duration,
        ...context
      });

      return healthResult;
    }
  }

  /**
   * Run all registered health checks
   */
  static async runAllChecks(context?: LogContext): Promise<SystemHealth> {
    const startTime = performance.now();
    const sessionId = debug.session.createSession({
      operation: 'health_check_all',
      ...context
    });

    logger.info('Running all health checks', {
      totalChecks: this.checks.size,
      ...context
    });

    const checkPromises = Array.from(this.checks.keys()).map(checkId => 
      this.runCheck(checkId, { ...context, sessionId })
    );

    const results = await Promise.allSettled(checkPromises);
    const checkResults = new Map<HealthCheckId, HealthCheckResult>();

    results.forEach((result, index) => {
      const checkId = Array.from(this.checks.keys())[index];
      if (result.status === 'fulfilled') {
        checkResults.set(checkId, result.value);
      } else {
        const errorResult: HealthCheckResult = {
          status: 'unhealthy',
          message: `Health check execution failed: ${result.reason}`,
          duration: 0,
          timestamp: Date.now(),
          error: errorHandling.createError('UNKNOWN', result.reason)
        };
        checkResults.set(checkId, errorResult);
      }
    });

    const summary = this.calculateSummary(checkResults);
    const overall = this.calculateOverallStatus(summary);
    const criticalIssues = this.getCriticalIssues(checkResults);

    const systemHealth: SystemHealth = {
      overall,
      checks: checkResults,
      summary,
      criticalIssues,
      timestamp: Date.now()
    };

    const duration = performance.now() - startTime;
    logger.info('All health checks completed', {
      overall,
      summary,
      criticalIssues: criticalIssues.length,
      duration,
      ...context
    });

    return systemHealth;
  }

  /**
   * Get current system health
   */
  static getCurrentHealth(): SystemHealth {
    const summary = this.calculateSummary(this.results);
    const overall = this.calculateOverallStatus(summary);
    const criticalIssues = this.getCriticalIssues(this.results);

    return {
      overall,
      checks: new Map(this.results),
      summary,
      criticalIssues,
      timestamp: Date.now()
    };
  }

  private static calculateSummary(results: Map<HealthCheckId, HealthCheckResult>) {
    let healthy = 0;
    let degraded = 0;
    let unhealthy = 0;
    let unknown = 0;

    results.forEach(result => {
      switch (result.status) {
        case 'healthy':
          healthy++;
          break;
        case 'degraded':
          degraded++;
          break;
        case 'unhealthy':
          unhealthy++;
          break;
        case 'unknown':
          unknown++;
          break;
      }
    });

    return {
      total: results.size,
      healthy,
      degraded,
      unhealthy,
      unknown
    };
  }

  private static calculateOverallStatus(summary: ReturnType<typeof this.calculateSummary>): HealthStatus {
    if (summary.unhealthy > 0) return 'unhealthy';
    if (summary.degraded > 0) return 'degraded';
    if (summary.healthy > 0) return 'healthy';
    return 'unknown';
  }

  private static getCriticalIssues(results: Map<HealthCheckId, HealthCheckResult>): HealthCheckResult[] {
    const criticalIssues: HealthCheckResult[] = [];
    
    results.forEach((result, checkId) => {
      const check = this.checks.get(checkId);
      if (check?.critical && (result.status === 'unhealthy' || result.status === 'degraded')) {
        criticalIssues.push(result);
      }
    });

    return criticalIssues;
  }
}

// Diagnostic manager
export class DiagnosticManager {
  private static readonly diagnostics = new Map<DiagnosticId, Diagnostic>();
  private static readonly results = new Map<DiagnosticId, DiagnosticResult>();

  /**
   * Register a diagnostic
   */
  static register(diagnostic: Diagnostic): void {
    this.diagnostics.set(diagnostic.id, diagnostic);
    logger.debug('Diagnostic registered', {
      diagnosticId: diagnostic.id,
      name: diagnostic.name,
      category: diagnostic.category
    });
  }

  /**
   * Run a specific diagnostic
   */
  static async runDiagnostic(diagnosticId: DiagnosticId, context?: LogContext): Promise<DiagnosticResult> {
    const diagnostic = this.diagnostics.get(diagnosticId);
    if (!diagnostic) {
      throw new Error(`Diagnostic not found: ${diagnosticId}`);
    }

    const startTime = performance.now();
    const sessionId = debug.session.createSession({
      operation: 'diagnostic',
      diagnosticId,
      diagnosticName: diagnostic.name,
      ...context
    });

    try {
      logger.debug('Running diagnostic', {
        diagnosticId,
        name: diagnostic.name,
        category: diagnostic.category,
        ...context
      });

      const result = await diagnostic.run({ ...context, sessionId });
      const duration = performance.now() - startTime;

      const diagnosticResult: DiagnosticResult = {
        ...result,
        duration,
        timestamp: Date.now()
      };

      this.results.set(diagnosticId, diagnosticResult);

      logger.debug('Diagnostic completed', {
        diagnosticId,
        name: diagnostic.name,
        status: result.status,
        duration,
        ...context
      });

      return diagnosticResult;
    } catch (error) {
      const duration = performance.now() - startTime;
      
      debug.session.addError(sessionId, error as Error, {
        diagnosticId,
        diagnosticName: diagnostic.name,
        duration
      });

      const diagnosticResult: DiagnosticResult = {
        status: 'failed',
        message: `Diagnostic failed: ${(error as Error).message}`,
        duration,
        timestamp: Date.now(),
        recommendations: ['Check diagnostic implementation', 'Review error logs']
      };

      this.results.set(diagnosticId, diagnosticResult);

      logger.error('Diagnostic failed', {
        diagnosticId,
        name: diagnostic.name,
        error: (error as Error).message,
        duration,
        ...context
      });

      return diagnosticResult;
    }
  }

  /**
   * Run all diagnostics in a category
   */
  static async runCategoryDiagnostics(category: Diagnostic['category'], context?: LogContext): Promise<DiagnosticResult[]> {
    const categoryDiagnostics = Array.from(this.diagnostics.values())
      .filter(d => d.category === category);

    logger.info('Running category diagnostics', {
      category,
      count: categoryDiagnostics.length,
      ...context
    });

    const results = await Promise.allSettled(
      categoryDiagnostics.map(d => this.runDiagnostic(d.id, context))
    );

    return results
      .filter((result): result is PromiseFulfilledResult<DiagnosticResult> => result.status === 'fulfilled')
      .map(result => result.value);
  }
}

// Built-in health checks
export const createSystemHealthChecks = (): HealthCheck[] => [
  {
    id: 'memory_usage' as HealthCheckId,
    name: 'Memory Usage',
    description: 'Check system memory usage',
    category: 'system',
    critical: true,
    timeout: 5000,
    check: async (): Promise<HealthCheckResult> => {
      const usage = process.memoryUsage();
      const heapUsedMB = usage.heapUsed / 1024 / 1024;
      const heapTotalMB = usage.heapTotal / 1024 / 1024;
      const usagePercentage = (heapUsedMB / heapTotalMB) * 100;

      if (usagePercentage > 90) {
        return {
          status: 'unhealthy',
          message: `Memory usage critical: ${usagePercentage.toFixed(1)}%`,
          details: { heapUsedMB, heapTotalMB, usagePercentage }
        };
      } else if (usagePercentage > 75) {
        return {
          status: 'degraded',
          message: `Memory usage high: ${usagePercentage.toFixed(1)}%`,
          details: { heapUsedMB, heapTotalMB, usagePercentage }
        };
      }

      return {
        status: 'healthy',
        message: `Memory usage normal: ${usagePercentage.toFixed(1)}%`,
        details: { heapUsedMB, heapTotalMB, usagePercentage }
      };
    }
  },
  {
    id: 'cpu_usage' as HealthCheckId,
    name: 'CPU Usage',
    description: 'Check CPU usage',
    category: 'system',
    critical: false,
    timeout: 5000,
    check: async (): Promise<HealthCheckResult> => {
      const startUsage = process.cpuUsage();
      await new Promise(resolve => setTimeout(resolve, 100));
      const endUsage = process.cpuUsage(startUsage);
      
      const cpuTime = (endUsage.user + endUsage.system) / 1000000; // Convert to seconds
      const cpuPercentage = (cpuTime / 0.1) * 100; // 100ms sample

      if (cpuPercentage > 80) {
        return {
          status: 'degraded',
          message: `CPU usage high: ${cpuPercentage.toFixed(1)}%`,
          details: { cpuPercentage, cpuTime }
        };
      }

      return {
        status: 'healthy',
        message: `CPU usage normal: ${cpuPercentage.toFixed(1)}%`,
        details: { cpuPercentage, cpuTime }
      };
    }
  },
  {
    id: 'disk_space' as HealthCheckId,
    name: 'Disk Space',
    description: 'Check available disk space',
    category: 'system',
    critical: true,
    timeout: 10000,
    check: async (): Promise<HealthCheckResult> => {
      try {
        const { promises: fs } = await import('fs');
        const stats = await fs.stat('.');
        
        // This is a simplified check - in production you'd use a proper disk space library
        return {
          status: 'healthy',
          message: 'Disk space check passed',
          details: { path: '.' }
        };
      } catch (error) {
        return {
          status: 'unhealthy',
          message: `Disk space check failed: ${(error as Error).message}`,
          details: { error: (error as Error).message }
        };
      }
    }
  }
];

// Built-in diagnostics
export const createSystemDiagnostics = (): Diagnostic[] => [
  {
    id: 'config_validation' as DiagnosticId,
    name: 'Configuration Validation',
    description: 'Validate system configuration',
    category: 'configuration',
    run: async (context?: LogContext): Promise<DiagnosticResult> => {
      try {
        // Check required environment variables
        const requiredEnvVars = ['NODE_ENV'];
        const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

        if (missingVars.length > 0) {
          return {
            status: 'failed',
            message: `Missing required environment variables: ${missingVars.join(', ')}`,
            recommendations: [
              'Set all required environment variables',
              'Check .env file configuration',
              'Verify deployment configuration'
            ]
          };
        }

        return {
          status: 'passed',
          message: 'Configuration validation passed',
          details: { checkedVariables: requiredEnvVars }
        };
      } catch (error) {
        return {
          status: 'failed',
          message: `Configuration validation failed: ${(error as Error).message}`,
          recommendations: ['Review configuration files', 'Check environment setup']
        };
      }
    }
  },
  {
    id: 'network_connectivity' as DiagnosticId,
    name: 'Network Connectivity',
    description: 'Test network connectivity',
    category: 'connectivity',
    run: async (context?: LogContext): Promise<DiagnosticResult> => {
      try {
        // Test basic connectivity
        const testUrls = ['https://httpbin.org/status/200', 'https://api.github.com'];
        const results = await Promise.allSettled(
          testUrls.map(url => fetch(url, { method: 'HEAD', signal: AbortSignal.timeout(5000) }))
        );

        const successful = results.filter(r => r.status === 'fulfilled').length;
        const successRate = (successful / testUrls.length) * 100;

        if (successRate < 50) {
          return {
            status: 'failed',
            message: `Network connectivity issues: ${successRate.toFixed(1)}% success rate`,
            details: { successRate, testedUrls: testUrls.length },
            recommendations: [
              'Check internet connection',
              'Verify firewall settings',
              'Test with different networks'
            ]
          };
        } else if (successRate < 100) {
          return {
            status: 'warning',
            message: `Partial network connectivity: ${successRate.toFixed(1)}% success rate`,
            details: { successRate, testedUrls: testUrls.length },
            recommendations: ['Monitor network stability', 'Check DNS resolution']
          };
        }

        return {
          status: 'passed',
          message: 'Network connectivity normal',
          details: { successRate, testedUrls: testUrls.length }
        };
      } catch (error) {
        return {
          status: 'failed',
          message: `Network connectivity test failed: ${(error as Error).message}`,
          recommendations: ['Check network configuration', 'Verify DNS settings']
        };
      }
    }
  }
];

// Troubleshooting coordinator
export class TroubleshootingCoordinator {
  /**
   * Run comprehensive system diagnostics
   */
  static async runFullDiagnostics(context?: LogContext): Promise<{
    health: SystemHealth;
    diagnostics: Map<DiagnosticId, DiagnosticResult>;
    summary: {
      overallHealth: HealthStatus;
      criticalIssues: number;
      failedDiagnostics: number;
      recommendations: string[];
    };
  }> {
    const sessionId = debug.session.createSession({
      operation: 'full_diagnostics',
      ...context
    });

    logger.info('Starting comprehensive system diagnostics', { ...context });

    // Run health checks
    const health = await HealthCheckManager.runAllChecks({ ...context, sessionId });

    // Run all diagnostics
    const diagnosticResults = new Map<DiagnosticId, DiagnosticResult>();
    const categories: Diagnostic['category'][] = ['performance', 'configuration', 'connectivity', 'data', 'security'];

    for (const category of categories) {
      const results = await DiagnosticManager.runCategoryDiagnostics(category, { ...context, sessionId });
      results.forEach(result => {
        // Find the diagnostic ID for this result
        const diagnostic = Array.from(DiagnosticManager['diagnostics'].values())
          .find(d => d.name === result.message.split(':')[0]); // This is a simplified approach
        if (diagnostic) {
          diagnosticResults.set(diagnostic.id, result);
        }
      });
    }

    // Generate recommendations
    const recommendations: string[] = [];
    
    if (health.criticalIssues.length > 0) {
      recommendations.push('Address critical health issues immediately');
    }
    
    const failedDiagnostics = Array.from(diagnosticResults.values())
      .filter(result => result.status === 'failed');
    
    if (failedDiagnostics.length > 0) {
      recommendations.push('Review and fix failed diagnostics');
    }

    if (health.overall === 'degraded') {
      recommendations.push('Monitor system performance closely');
    }

    const summary = {
      overallHealth: health.overall,
      criticalIssues: health.criticalIssues.length,
      failedDiagnostics: failedDiagnostics.length,
      recommendations
    };

    logger.info('Comprehensive diagnostics completed', {
      ...summary,
      ...context
    });

    return {
      health,
      diagnostics: diagnosticResults,
      summary
    };
  }

  /**
   * Get system status summary
   */
  static getSystemStatus(): {
    health: SystemHealth;
    uptime: number;
    memoryUsage: NodeJS.MemoryUsage;
    version: string;
    environment: string;
  } {
    return {
      health: HealthCheckManager.getCurrentHealth(),
      uptime: process.uptime(),
      memoryUsage: process.memoryUsage(),
      version: process.version,
      environment: process.env.NODE_ENV || 'development'
    };
  }
}

// Initialize built-in health checks and diagnostics
export const initializeTroubleshooting = (): void => {
  // Register built-in health checks
  createSystemHealthChecks().forEach(check => {
    HealthCheckManager.register(check);
  });

  // Register built-in diagnostics
  createSystemDiagnostics().forEach(diagnostic => {
    DiagnosticManager.register(diagnostic);
  });

  logger.info('Troubleshooting system initialized', {
    healthChecks: createSystemHealthChecks().length,
    diagnostics: createSystemDiagnostics().length
  });
};

// Export main troubleshooting utilities
export const troubleshooting = {
  health: HealthCheckManager,
  diagnostics: DiagnosticManager,
  coordinator: TroubleshootingCoordinator,
  initialize: initializeTroubleshooting
};
